const { debounce, getElement, getElements } = window.BAO.Utils

class CollectionFiltersRenderer {
  /**
   * @param {CollectionFiltersForm} form
   */
  constructor (form) {
    /**
     * @type {CollectionFiltersForm}
     * @public
     */
    this.form = form
  }

  /**
   * @param {Function} filterDataUrl
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  fromCache (filterDataUrl, e) {
    const html = this.form.filterData.find(element => filterDataUrl(element))
      .html

    this.form.log('fromCache', { filterDataUrl, e, html })

    this.render(html, e)
  }

  /**
   * @param {String} section
   * @param {String} url
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  async fromFetch (section, url, e) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.form.log('fromFetch', { section, url, html })

    this.form.filterData.push({ html, url })

    this.render(html, e)
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  render (html, e) {
    this.renderFilters(html, e)

    if (this.form.shouldHoldResults) {
      this.form.pendingResultsHtml = html
    } else {
      this.form.pendingResultsHtml = null
      this.renderResults(html)
    }

    this.form.loading = false
  }

  /**
   * @param {String} html
   *
   * @returns {void}
   */
  renderResults (html) {
    this.renderProductGridContainer(html)
    this.renderProductCount(html)
    this.refreshYotpoWidgets()
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  renderFilters (html, e) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
    const detailSelector = [
      this.form.els?.facet?.selector,
      this.form.externalEls?.facet?.selector,
    ]
      .filter(Boolean)
      .join(',')
    const facetDetailElements = getElements(detailSelector, {
      context: parsedHTML,
    })
    /**
     * @param {HTMLElement} element
     *
     * @returns {Boolean}
     */
    const matchesIndex = element => {
      const facet = e ? e.target.closest(detailSelector) : null

      return facet ? element.dataset.index === facet.dataset.index : false
    }
    const facetsToRender = facetDetailElements.filter(el => !matchesIndex(el))

    this.form.log('renderFilters', {
      e,
      parsedHTML,
      facetDetailElements,
      facetsToRender,
    })

    facetsToRender.forEach(element => {
      const selector = detailSelector
        .split(',')
        .map(selector => `${selector}[data-index="${element.dataset.index}"]`)
        .join(',')

      const el = getElement(selector)

      el.innerHTML = element.innerHTML
    })

    this.renderActiveFacets(parsedHTML)
    this.renderAdditionalElements(parsedHTML)
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderActiveFacets (parsedHTML) {
    this.activeFacetSelectors.forEach(selector => {
      const activeFacetElement = parsedHTML.querySelector(selector)

      this.form.log('renderActiveFacets', {
        parsedHTML,
        activeFacetElement,
        selector,
      })

      if (activeFacetElement.children[0].childElementCount >= 1) {
        document
          .querySelector('.clc-ProductGridFacetsMobile_ActiveFacet-clear')
          .setAttribute('aria-hidden', 'false')
      } else {
        document
          .querySelector('.clc-ProductGridFacetsMobile_ActiveFacet-clear')
          .setAttribute('aria-hidden', 'true')
      }

      if (!activeFacetElement) return

      getElements(selector).forEach(
        el => (el.innerHTML = activeFacetElement.innerHTML),
      )
    })
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderAdditionalElements (parsedHTML) {
    const facetsMobile = parsedHTML.querySelector(
      '.clc-ProductGrid_FacetsMobile',
    )

    getElements('.clc-ProductGrid_FacetsMobile').forEach(
      el => (el.innerHTML = facetsMobile.innerHTML),
    )
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderSortElements (parsedHTML) {
    const sortMobile = parsedHTML.querySelector(
      '.clc-ProductGridFacetMobile_List',
    )
    const sortDesktop = parsedHTML.querySelector('.clc-ProductGridFacet_List')

    getElements('.clc-ProductGrid_FacetsMobile').forEach(
      el => (el.innerHTML = sortMobile.innerHTML),
    )

    getElements('.clc-ProductGridFacet_List').forEach(
      el => (el.innerHTML = sortDesktop.innerHTML),
    )
  }

  /**
   * @param {String} html
   *
   * @returns {void}
   */
  renderProductGridContainer (html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    this.form.externalEls['product-grid'].element.innerHTML = getElement(
      this.form.externalEls['product-grid'].selector,
      { context: parsedHTML },
    ).innerHTML
  }

  /**
   * @param {String} html
   *
   * @returns {void}
   */
  renderProductCount (html) {
    const count = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[data-product-grid-count]')?.innerHTML
    this.form.log('renderProductCount', {
      html,
      count,
      countElements: this.countElements,
    })
    if (!count) return

    this.countElements.forEach(element => (element.innerHTML = count))
  }

  /**
   * @returns {Element[]}
   */
  get countElements () {
    return getElements('[data-product-grid-count]')
  }

  /**
   * @returns {String[]}
   */
  get activeFacetSelectors () {
    if ('active-facets' in this.form.externalEls) {
      return [
        this.form.els['active-facets'].selector,
        this.form.externalEls['active-facets'].selector,
      ]
    }

    return [this.form.els['active-facets'].selector]
  }

  /**
   * Refreshes Yotpo widgets on the webpage.
   * @function refreshYotpoWidgets
   */

  refreshYotpoWidgets () {
    if (!window.yotpo?.initialized) return
    window.yotpo.refreshWidgets()
  }
}

/**
 * @typedef {Object} FilterData
 *
 * @property {string} html
 * @property {string} url
 */

class CollectionFiltersForm extends window.BAO.CustomElement({
  SuperClass: HTMLFormElement,
}) {
  static get requiredElements () {
    return ['facets', 'facet', 'facet-summary']
  }

  static get requiredExternalElements () {
    return ['container', 'product-grid']
  }

  constructor () {
    super()

    /**
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: 'template-collection-product-grid',
        section: this.dataset.id,
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {FilterData[]}
     * @public
     */
    this.filterData = []

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }

    /**
     * @type {CollectionFiltersRenderer}
     * @public
     */
    this.renderer = new CollectionFiltersRenderer(this)

    /**
     * @type {String|null}
     * @public
     */
    this.pendingResultsHtml = null
  }

  setupListeners () {
    super.setupListeners()

    this.onFacetRemovalClick = this.onFacetRemovalClick.bind(this)

    this.debouncedOnSubmit = debounce(e => this.onSubmit(e), 500)
    this.listeners.add(this, 'input', this.debouncedOnSubmit.bind(this))

    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))
    this.listeners.add(
      this.els.facets.element,
      'keyup',
      this.onKeyUp.bind(this),
    )

    this.listeners.add(document, 'click', this.onPaginationClick.bind(this))
    this.listeners.add(document, 'click', this.onDrawerRelease.bind(this))
    this.listeners.add(document, 'click', this.onFacetDrawerOpen.bind(this))

    this.handleDescriptionClick = this.handleDescriptionClick.bind(this)

    if (
      !this.externalEls.shortDescription ||
      !this.externalEls.longDescription
    ) {
      return
    }

    if (this?.externalEls?.showMore?.exists) {
      this.listeners.add(
        this.externalEls.showMore.element,
        'click',
        this.handleDescriptionClick,
      )
    }
    if (this?.externalEls?.showLess?.exists) {
      this.listeners.add(
        this.externalEls.showLess.element,
        'click',
        this.handleDescriptionClick,
      )
    }
  }

  /**
   * @param {Event} e
   *
   * @returns {void}
   */
  handleDescriptionClick (event) {
    if (event.target.closest(this.externalEls.showMore.selector)) {
      this.showMore()
    }
    if (event.target.closest(this.externalEls.showLess.selector)) {
      this.showLess()
    }
  }

  get hiddenAttribute () {
    return `aria-hidden`
  }

  showMore () {
    this.externalEls.shortDescription.element.setAttribute(
      this.hiddenAttribute,
      true,
    )
    this.externalEls.longDescription.element.setAttribute(
      this.hiddenAttribute,
      false,
    )
  }

  showLess () {
    this.externalEls.shortDescription.element.setAttribute(
      this.hiddenAttribute,
      false,
    )
    this.externalEls.longDescription.element.setAttribute(
      this.hiddenAttribute,
      true,
    )
  }

  /**
   * @param {PopStateEvent} e
   *
   * @returns {void}
   */
  onHistoryChange (e) {
    const searchParams = e.state
      ? e.state.searchParams
      : this.searchParams.initial
    if (searchParams.toString() === this.searchParams.previous.toString()) {
      return
    }

    this.renderPage({ searchParams, updateURLHash: false })
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onFacetRemovalClick (e) {
    e.preventDefault()

    const searchParams = e.currentTarget.href.includes('?')
      ? e.currentTarget.href.slice(e.currentTarget.href.indexOf('?') + 1)
      : ''

    this.log('onFacetRemovalClick', { searchParams })

    this.renderPage({ searchParams })
  }

  /**
   * @param {KeyboardEvent} e
   *
   * @returns {void}
   */
  onKeyUp (e) {
    if (e.code.toUpperCase() !== 'ESCAPE') return

    const openFacet = e.target.closest(`${this.els.facet.selector}[open]`)
    if (!openFacet) return

    const summary = getElement(`${this.els['facet-summary'].selector}`, {
      context: openFacet,
    })
    openFacet.removeAttribute('open')
    summary.setAttribute('aria-expanded', 'false')
    summary?.focus()
  }

  /**
   * Apply, close and backdrop clicks all close the drawer, so they all release the held results.
   *
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onDrawerRelease (e) {
    if (this.pendingResultsHtml === null) return
    if (!e.target.closest(this.drawerReleaseSelector)) return

    const html = this.pendingResultsHtml
    this.pendingResultsHtml = null

    this.renderer.renderResults(html)
  }

  /**
   * Desktop facet labels open the filter drawer with the matching facet expanded.
   *
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onFacetDrawerOpen (e) {
    const drawer = this.filterDrawerEl
    const facet = e.target.closest('.clc-ProductGridFacet-drawer')

    if (!drawer || !facet) return

    const index = facet.dataset.index.replace(/^[^-]+-/, '')

    drawer.querySelectorAll('details[data-index]').forEach(details => {
      details.open =
        details.dataset.index.replace(/^[^-]+-Mobile-|^[^-]+-/, '') === index
    })
  }

  get drawerReleaseSelector () {
    const key = this.filterDrawerEl?.getAttribute('key')

    return [
      `[data-drawers-trigger="${key}"]`,
      '[data-drawers-close]',
      '[data-site-drawers-el="backdrop"]',
    ].join(',')
  }

  /**
   * @returns {HTMLElement|undefined}
   */
  get filterDrawerEl () {
    return this.els?.['filter-drawer']?.element
  }

  /**
   * @returns {Boolean}
   */
  get shouldHoldResults () {
    return Boolean(this.filterDrawerEl?.isOpen)
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onPaginationClick (e) {
    if (!e.target.closest(this.externalEls?.pagination?.selector)) return

    e.preventDefault()

    const anchor = e.target.closest('a')
    this.log('onPaginationClick', { anchor })
    if (!anchor || anchor.getAttribute('href') === '' || anchor.disabled) return

    const href = anchor.getAttribute('href')
    this.log('onPaginationClick', { href })
    // eslint-disable-next-line compat/compat
    const paginationSearchParams = new URLSearchParams(href.split('?')[1])
    const wantedPage = paginationSearchParams.get('page') ?? '1'
    // eslint-disable-next-line compat/compat
    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', wantedPage)

    this.renderPage({ searchParams: currentSearchParams.toString() })
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onSubmit (e) {
    e.preventDefault()

    if (e.target.name === 'sort_by') {
      const sortOptions = document.querySelectorAll(
        '.clc-ProductGridFacet_Checkbox[name="sort_by"]',
      )
      sortOptions.forEach(option => {
        if (option.checked && option.value !== e.target.value) {
          option.checked = false
        }

        if (option.value === e.target.value) {
          option.checked = true
        }
      })
    }

    const formData = new FormData(this)
    // eslint-disable-next-line compat/compat
    const searchParams = new URLSearchParams(formData).toString()

    this.renderPage({ searchParams, e })
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {InputEvent} [e]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  renderPage ({ searchParams, e, updateURLHash = true } = {}) {
    this.log('renderPage', { searchParams, e, updateURLHash })

    this.searchParams.previous = this.searchParams.current
    // eslint-disable-next-line compat/compat
    this.searchParams.current = new URLSearchParams(searchParams)

    // Results are held while the drawer is open, so there is nothing to show as loading
    this.loading = !this.shouldHoldResults

    this.sections.forEach(section => {
      const url = `${window.location.pathname}?section_id=${
        section.section
      }&${this.searchParams.current.toString()}`
      /**
       * @param {FilterData} element
       *
       * @returns {Boolean}
       */
      const filterDataUrl = element => element.url === url

      this.log('renderPageSections', { section, url })

      this.filterData.some(element => filterDataUrl(element))
        ? this.renderer.fromCache(filterDataUrl, e)
        : this.renderer.fromFetch(section.section, url, e)
    })

    if (updateURLHash) this.updateURLHash(this.searchParams.current)
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${searchParams &&
        '?'.concat(searchParams.toString())}`,
    )
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this._loading
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this._loading = val

    this.setAttribute('aria-busy', val.toString())

    this.externalEls?.container.element.setAttribute(
      'aria-busy',
      val.toString(),
    )
  }
}

if (!customElements.get('collection-filters-form')) {
  customElements.define('collection-filters-form', CollectionFiltersForm, {
    extends: 'form',
  })
}

class PriceRange extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['min', 'max']
  }

  constructor () {
    super()

    this.setMinAndMaxValues()
  }

  setupListeners () {
    super.setupListeners()

    this.onRangeChange = this.onRangeChange.bind(this)

    getElements('input', { context: this }).forEach(el =>
      this.listeners.add(el, 'change', this.onRangeChange),
    )
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onRangeChange (e) {
    this.adjustToValidValues(e.currentTarget)
    this.setMinAndMaxValues()
  }

  setMinAndMaxValues () {
    if (this.maxInput.value) {
      this.minInput.setAttribute('max', this.maxInput.value)
    }
    if (this.minInput.value) {
      this.maxInput.setAttribute('min', this.minInput.value)
    }
    if (this.minInput.value === '') this.maxInput.setAttribute('min', '0')
    if (this.maxInput.value === '') {
      this.minInput.setAttribute('max', this.maxInput.getAttribute('max'))
    }
  }

  /**
   * @param {HTMLInputElement} input
   *
   * @returns {void}
   */
  adjustToValidValues (input) {
    const value = Number(input.value)
    const min = Number(input.getAttribute('min'))
    const max = Number(input.getAttribute('max'))

    if (value < min) input.value = min.toString()
    if (value > max) input.value = max.toString()
  }

  /**
   * @returns {HTMLInputElement}
   */
  get maxInput () {
    return this.els.max.element
  }

  /**
   * @returns {HTMLInputElement}
   */
  get minInput () {
    return this.els.min.element
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange)
}

class FacetRemove extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this.querySelector('a'), 'click', e => {
      e.preventDefault()

      this.form.onFacetRemovalClick(e)
    })
  }

  get form () {
    const selector = 'collection-filters-form, [is="collection-filters-form"]'

    return this.closest(selector) || document.querySelector(selector)
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove)
}

class FilterFacet extends window.BAO.CustomElement({
  SuperClass: HTMLDetailsElement,
}) {
  static get requiredElements () {
    return ['summary', 'content']
  }

  constructor () {
    super()

    this.isTouch = false
    this.keepOpen = false
  }

  setupListeners () {
    super.setupListeners()
  }

  get content () {
    return window.BAO.Utils.getElement(this.els.content.selector, {
      context: this,
    })
  }
}

if (!customElements.get('filter-facet')) {
  customElements.define('filter-facet', FilterFacet, { extends: 'details' })
}
